/* eslint-disable jsx-a11y/accessible-emoji */
import { compose } from '@doltech/utils/lib/compose';
import cl from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';

import { colorsV2 } from '../../../../../colors-v2';
import { Typography } from '../../../../../DesignSystemV2/Typography';
import { findBellowByType } from '../../../../../DolEditor/helpers/nodeValue';
import { DrawerType, OptionsListDrawer } from '../../../../../Mobile/OptionsList/OptionsListDrawer';
import { useBlankV2DoTestStore } from '../../../../plugins-common/BlankV2/store/do-test.store';
import { CORRECT_ANSWER } from '../../../../plugins-common/CorrectAnswers/types';
import { useExerciseGapFillWithHintDoTestStore } from '../../store/exercise-gap-fill-with-hint-do-test.store';
import { DoTestActions } from './DoTestActions';
import { useRootElement } from '../../../../../../slatePlugins/common/hocs/current-element.store';

const Main = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
  margin-top: 32px;

  .option-list {
    display: flex;
    flex-wrap: wrap;
    margin: -8px -4px;

    > * {
      margin: 8px 4px;
    }

    &.hidden {
      display: none;
    }
  }

  .result-list {
    display: grid;
    width: 100%;
    gap: 16px;
    grid-template-columns: minmax(0, 1fr);

    .result-item {
      display: grid;
      gap: 8px;
      grid-template-columns: 24px minmax(0, 1fr);
      align-items: center;

      .circle-text {
        width: 24px;
        height: 24px;
        display: grid;
        place-items: center;
        background-color: ${colorsV2.gray100};
        border-radius: 50%;
        color: ${colorsV2.white100};
        line-height: 0;

        .ant-typography {
          line-height: 0;
        }
      }
    }
  }
`;

const ResultOptions = observer((props: RenderElementProps) => {
  const store = useExerciseGapFillWithHintDoTestStore();
  const parentNode = useRootElement();
  const resultOptions = React.useMemo(() => {
    const correctAnswerNodeEntries = findBellowByType(parentNode, CORRECT_ANSWER);

    return correctAnswerNodeEntries.map((node, index) => {
      const { correctAnswer } = node;
      return {
        key: correctAnswer,
        order: (parentNode?.startFrom || 1) + index,
        displayName: store.dropdownOptions.get(correctAnswer)?.displayName,
      };
    });
  }, [parentNode, store.dropdownOptions]);
  return (
    <Main contentEditable={false} onMouseDown={(e) => e.preventDefault()}>
      <Typography.Paragraph color="green100" variant="semi-bold/16">
        ✅️ &nbsp;Đáp án
      </Typography.Paragraph>
      <div className="result-list">
        {resultOptions.map((resultOption) => {
          const { key, order, displayName } = resultOption;
          return (
            <div className="result-item" key={key}>
              <div className="circle-text">
                <Typography.Text variant="semi-bold/12">{order}</Typography.Text>
              </div>
              <Typography.Paragraph variant="medium/16" className="result-text">
                {displayName}
              </Typography.Paragraph>
            </div>
          );
        })}
      </div>
    </Main>
  );
});

export const DoTestExerciseGapFillWithHintOptionsBase = (props: RenderElementProps) => {
  const { children, attributes } = props;
  const store = useExerciseGapFillWithHintDoTestStore();
  const gapFillStore = useExerciseGapFillWithHintDoTestStore();
  const blankStore = useBlankV2DoTestStore();

  const options = (gapFillStore?.gapFillOptions || []).map((option) => {
    return {
      key: option.id,
      text: option.displayName,
      value: option.displayName,
    };
  });

  if (!gapFillStore) {
    return null;
  }

  return (
    <Main className="do-test-exercise-gap-fill-w-hint-options" {...attributes}>
      {store?.isViewResult ? (
        <ResultOptions {...props} />
      ) : (
        <div contentEditable={false} onMouseDown={(e) => e.preventDefault()}>
          <Typography.Text color="gray100" variant="semi-bold/16">
            💡 Gợi ý
          </Typography.Text>
        </div>
      )}

      <div className={cl('option-list', { hidden: store?.isViewResult })}>{children}</div>
      <DoTestActions {...props} />
      <OptionsListDrawer
        type={DrawerType.TEXT_ONLY}
        title="Option list"
        selectedOpt={null}
        visible={gapFillStore?.drawerOpen}
        options={options}
        onItemClick={(item: any) => {
          blankStore.update({ id: gapFillStore.currentBlank, userAnswer: item.key });
          gapFillStore.toggleDrawer(false);
          gapFillStore.setCurrentBlank(null);
        }}
        onClose={() => {
          gapFillStore.toggleDrawer(false);
        }}
      />
    </Main>
  );
};

export const DoTestExerciseGapFillWithHintOptions = compose(observer)(
  DoTestExerciseGapFillWithHintOptionsBase
);
